import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search as SearchIcon, Phone, Mail, MapPin, Filter, X } from "lucide-react";

const STAGES = [
  "new_lead", "contacted", "estimate_scheduled", "estimate_sent",
  "waiting_approval", "approved", "scheduled", "in_progress", "completed", "paid"
];
const FENCE_TYPES = ["wood", "cedar", "chain_link", "ornamental_iron", "vinyl"];

const STAGE_COLORS = {
  new_lead: "bg-blue-100 text-blue-700",
  contacted: "bg-violet-100 text-violet-700",
  estimate_scheduled: "bg-amber-100 text-amber-700",
  estimate_sent: "bg-orange-100 text-orange-700",
  waiting_approval: "bg-red-100 text-red-700",
  approved: "bg-emerald-100 text-emerald-700",
  scheduled: "bg-cyan-100 text-cyan-700",
  in_progress: "bg-orange-100 text-orange-700",
  completed: "bg-green-100 text-green-700",
  paid: "bg-green-200 text-green-800",
};

export default function SearchPage() {
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [fenceFilter, setFenceFilter] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.append("search", query);
      if (stageFilter && stageFilter !== "all_stages") params.append("stage", stageFilter);
      if (fenceFilter && fenceFilter !== "all_types") params.append("fence_type", fenceFilter);
      const { data } = await api.get(`/leads?${params.toString()}`);
      setLeads(data);
    } catch (err) { console.error("Search failed:", err); }
    finally { setLoading(false); }
  }, [query, stageFilter, fenceFilter]);

  useEffect(() => {
    const timer = setTimeout(fetchLeads, 300);
    return () => clearTimeout(timer);
  }, [fetchLeads]);

  const clearFilters = () => { setStageFilter(""); setFenceFilter(""); setQuery(""); };

  return (
    <div className="space-y-4" data-testid="search-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 tracking-tight">Search</h1>
        <p className="text-sm text-zinc-500 mt-1">Find leads and jobs</p>
      </div>

      {/* Search Bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, phone, address..."
            className="pl-10 border-zinc-300"
            data-testid="search-input"
          />
        </div>
        <Button
          variant={showFilters ? "default" : "outline"}
          onClick={() => setShowFilters(!showFilters)}
          className={showFilters ? "bg-orange-600 hover:bg-orange-700 text-white" : ""}
          data-testid="toggle-filters-btn"
        >
          <Filter className="w-4 h-4" />
        </Button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="flex flex-wrap gap-3 p-3 bg-zinc-50 border border-zinc-200 rounded-lg animate-fade-in">
          <div className="w-44">
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger data-testid="filter-stage"><SelectValue placeholder="All Stages" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all_stages">All Stages</SelectItem>
                {STAGES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-44">
            <Select value={fenceFilter} onValueChange={setFenceFilter}>
              <SelectTrigger data-testid="filter-fence"><SelectValue placeholder="All Fence Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all_types">All Types</SelectItem>
                {FENCE_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(stageFilter || fenceFilter || query) && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-zinc-500" data-testid="clear-filters-btn">
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
        </div>
      )}

      {/* Results */}
      <div className="space-y-2" data-testid="search-results">
        {loading ? (
          <Card className="border border-zinc-200 shadow-none"><CardContent className="p-8 text-center"><p className="text-sm text-zinc-400">Searching...</p></CardContent></Card>
        ) : leads.length === 0 ? (
          <Card className="border border-zinc-200 shadow-none"><CardContent className="p-8 text-center">
            <SearchIcon className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">{query || stageFilter || fenceFilter ? "No results found" : "Start searching"}</p>
          </CardContent></Card>
        ) : (
          <>
            <p className="text-xs text-zinc-400">{leads.length} results</p>
            {leads.map((lead) => (
              <button
                key={lead.id}
                onClick={() => navigate(`/leads/${lead.id}`)}
                className="w-full flex items-center gap-4 p-4 bg-white border border-zinc-200 rounded-lg hover:border-orange-300 transition-colors text-left"
                data-testid={`search-result-${lead.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-orange-700">{lead.name?.[0]?.toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-zinc-900">{lead.name}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-1">
                    {lead.phone && <span className="text-xs text-zinc-500 flex items-center gap-0.5"><Phone className="w-3 h-3" /> {lead.phone}</span>}
                    {lead.email && <span className="text-xs text-zinc-500 flex items-center gap-0.5"><Mail className="w-3 h-3" /> {lead.email}</span>}
                    {lead.address && <span className="text-xs text-zinc-500 flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {lead.address}</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <Badge className={`text-xs capitalize ${STAGE_COLORS[lead.stage] || ""}`}>{lead.stage?.replace(/_/g, " ")}</Badge>
                  {lead.fence_type && <span className="text-xs text-zinc-400 capitalize">{lead.fence_type.replace(/_/g, " ")}</span>}
                </div>
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
